import ActionCable from 'actioncable'
import User from '@/store/models/User'
import UserNotification from '@/store/models/UserNotification'
import GroupChatPost from '@/store/models/GroupChatPost'
import PrivateChat from '@/store/models/PrivateChat'
import PrivateChatPost from '@/store/models/PrivateChatPost'
import VideoRoom from '@/store/models/VideoRoom'
import TaskCard from '@/store/models/TaskCard'
import Push from 'push.js'
import i18n from '@/i18n'

const defaultState = () => {
  return {
    cable: null,
    organizationChannel: null,
    keepOnlineInterval: null,
    updatedViaChannel: null // チャンネル経由で更新された情報を扱う
  }
}

const state = defaultState()

const getters = {
  updatedViaChannel: state => state.updatedViaChannel
}

const mutations = {
  cable(state, payload) {
    state.cable = payload
  },
  organizationChannel(state, payload) {
    state.organizationChannel = payload
  },
  keepOnlineInterval(state, payload) {
    state.keepOnlineInterval = payload
  },
  updatedViaChannel(state, payload) {
    state.updatedViaChannel = payload
  },
  resetModule(state) {
    Object.assign(state, defaultState())
  }
}

function setDesktopNotification(rootData) {
  if (rootData) {
    const data = rootData.body.data
    const sender = rootData.body.included?.find((item) => item.type === "user")

    let title = ''
    let message = ''
    let url = process.env.VUE_APP_URL

    switch(data.attributes.notificationType){
      case 'groupChat':
        title = i18n.t('common.notifications.groupChat.title')
        if (sender) {
          title += ' ' + i18n.t('common.notifications.groupChat.titleSender', { sender: sender.attributes.meta.displayName})
        }
        message =  i18n.t('common.notifications.groupChat.message',{ room: data.attributes.title })
        url = url + 'chat/' + data.attributes.relatedId + '?required_organization_id=' + rootData.organizationId
        break
      case 'projectChat':
        title = i18n.t('common.notifications.projectChat.title')
        if (sender) {
          title += ' ' + i18n.t('common.notifications.projectChat.titleSender', { sender: sender.attributes.meta.displayName})
        }
        message = i18n.t('common.notifications.projectChat.message',{ project: data.attributes.title })
        url = url + 'project/' + data.attributes.additinalId + '?default_menu=project_chat&required_organization_id=' + rootData.organizationId
        break
      case 'privateChat':
        title =  i18n.t('common.notifications.privateChat.title')
        if (sender) {
          title += ' ' + i18n.t('common.notifications.privateChat.titleSender', { sender: sender.attributes.meta.displayName})
        }
        message = i18n.t('common.notifications.privateChat.message')
        url = url + 'member/' + data.attributes.senderId + '?default_menu=private_chat&required_organization_id=' + rootData.organizationId
        break
      case 'taskCardAssignees':
        title = i18n.t('common.notifications.taskCardAssignees.title')
        if (sender) {
          title += ' ' + i18n.t('common.notifications.taskCardAssignees.titleSender', { sender: sender.attributes.meta.displayName})
        }
        message = data.attributes.title
        url = url + 'project/' + data.attributes.additinalId + '/task_cards/' + data.attributes.relatedId + '?required_organization_id=' + rootData.organizationId
        break
      case 'videoRoomMember':
        title = data.attributes.message
        message = data.attributes.title
        url = url + 'meeting/' + data.attributes.additinalId + '/reservations/' + data.attributes.relatedId + '?default_menu=project_chat&required_organization_id=' + rootData.organizationId
        break
      case 'cafeRoomMember':
        title = data.attributes.message
        message = ''
        if (sender) {
          message += ' ' + i18n.t('common.notifications.cafeRoomMember.messageSender', { sender: sender.attributes.meta.displayName})
        }
        url = url + 'meeting/' + data.attributes.additinalId + '/reservations/' + data.attributes.relatedId + '?default_menu=project_chat&required_organization_id=' + rootData.organizationId
        break
      case 'groupChatMember':
        title = data.attributes.message
        message = data.attributes.title
        url = url + 'chat/' + data.attributes.relatedId + '?required_organization_id=' + rootData.organizationId
        break
      case 'projectMember':
        title = data.attributes.message
        message = data.attributes.title
        url = url + 'project/' + data.attributes.relatedId + '?required_organization_id=' + rootData.organizationId
        break
    }

    Push.create(title,{
      body: message,
      icon: process.env.VUE_APP_URL + 'favicon.ico',
      onClick: function () {
        // location.href = url
        window.open(url)
        this.close()
      }
    })
  }
}

const actions = {
  async initCable({ commit, dispatch }) {
    await commit('cable', ActionCable.createConsumer(process.env.VUE_APP_ACTIONCABLE_END_POINT))
    dispatch('connectUserChannel')
    dispatch('connectOrganizationChannel')
  },
  clearCable({ state, commit }) {
    state.cable?.disconnect()
    clearInterval(state.keepOnlineInterval)

    commit('resetModule')
  },
  connectUserChannel({ state, commit, rootGetters, dispatch }) {
    state.cable.subscriptions.create(
      {
        channel: 'ClientUserChannel'
      },
      {
        connected: () => {
          const keepOnlineMessage = {
            command: 'message',
            identifier: JSON.stringify({ channel: 'ClientUserChannel' }),
            data: JSON.stringify({ action: 'keep_online' })
          }

          // NOTE: 570000 => 9分30秒
          if (state.keepOnlineInterval === null) {
            commit('keepOnlineInterval', setInterval(() => {
              state.cable.send(keepOnlineMessage)
            }, 570000))
          }
        },
        received: (data) => {
          // Desktop Notification
          if (data.type === 'userNotification') {
            if (data.body.data && data.body.data.attributes.isMentioned) {
              setDesktopNotification(data)
            }
          }
          if (!data.type || data.organizationId != rootGetters['auth/currentOrganization'].id) return

          switch (data.type) {
            case 'userNotification': {
              if (data.body.deletedUserNotificationtId) {
                UserNotification.delete(data.body.deletedUserNotificationtId)
              } else {
                if (data.body.data.attributes.notificationType === 'groupChat' || data.body.data.attributes.notificationType === 'privateChat') {
                  UserNotification.delete((record) => {
                    return record.notificationType === data.body.data.attributes.notificationType && record.relatedId === data.body.data.attributes.relatedId
                  })
                }
                UserNotification.insert({
                  data: Object.assign(data.body.data.attributes, {
                    senderId: data.body.data.relationships.sender.data.id
                    // toast: true
                  })
                })
              }
              break
            }
            case 'postGroupChat': {
              if (data.body.deletedGroupChatPostId) {
                const deletedGroupChatPost = GroupChatPost.find(data.body.deletedGroupChatPostId)
                if (deletedGroupChatPost) {
                  GroupChatPost.delete(deletedGroupChatPost.id)
                  dispatch('entities/groupChatPosts/reloadCurrentIndex', deletedGroupChatPost.groupChatId, { root: true })
                  commit('updatedViaChannel', {
                    type: 'groupChat',
                    id: deletedGroupChatPost.groupChatId
                  })
                }
              } else {
                let groupChatAttachmentsAttributes
                if (data.body.data.relationships.groupChatAttachments) {
                  const groupChatAttachmentIds = data.body.data.relationships.groupChatAttachments.data.map(groupChatAttachment => groupChatAttachment.id)
                  groupChatAttachmentsAttributes = data.body.included.filter(resource => resource.type === 'groupChatAttachment' && groupChatAttachmentIds.includes(resource.id))
                                                                     .map(groupChatAttachment => groupChatAttachment.attributes)
                }
                let groupChatReactions
                if (data.body.data.relationships.groupChatReactions) {
                  const groupChatReactionIds = data.body.data.relationships.groupChatReactions.data.map(groupChatReaction => groupChatReaction.id)
                  groupChatReactions = data.body.included.filter(resource => resource.type === 'groupChatReaction' && groupChatReactionIds.includes(resource.id))
                                                             .map(groupChatReaction => groupChatReaction.attributes)
                }
                GroupChatPost.insert({
                  data: Object.assign(data.body.data.attributes, {
                    user: data.body.included.filter(resource => resource.type === 'user' && resource.id == data.body.data.attributes.userId)[0].attributes,
                    groupChatAttachmentsAttributes: groupChatAttachmentsAttributes,
                    groupChatReactions: groupChatReactions
                  })
                })
                commit('updatedViaChannel', {
                  type: 'groupChat',
                  id: data.body.data.attributes.groupChatId
                })
              }
              break
            }
            case 'reactGroupChat': {
              let groupChatPost
              if (data.body.deletedGroupChatReactionId) {
                groupChatPost = GroupChatPost.find(data.body.groupChatPostId)
                if (groupChatPost) {
                  GroupChatPost.update({
                    where: data.body.groupChatPostId,
                    data(groupChatPost) {
                      groupChatPost.groupChatReactions = groupChatPost.groupChatReactions.filter(reaction => {
                        return reaction.id != data.body.deletedGroupChatReactionId
                      })
                    }
                  })
                }
              } else {
                groupChatPost = GroupChatPost.find(data.body.data.attributes.groupChatPostId)
                if (groupChatPost) {
                  GroupChatPost.update({
                    where: data.body.data.attributes.groupChatPostId,
                    data(groupChatPost) {
                      groupChatPost.groupChatReactions.push(data.body.data.attributes)
                    }
                  })
                }
              }
              if (groupChatPost) {
                commit('updatedViaChannel', {
                  type: 'groupChat',
                  id: groupChatPost.groupChatId
                })
              }
              break
            }
            case 'postPrivateChat': {
              if (data.body.deletedPrivateChatPostId) {
                const deletedPrivateChatPost = PrivateChatPost.find(data.body.deletedPrivateChatPostId)
                if (deletedPrivateChatPost) {
                  PrivateChatPost.delete(deletedPrivateChatPost.id)

                  const privateChat = PrivateChat.find(deletedPrivateChatPost.privateChatId)
                  if (privateChat) {
                    dispatch('entities/privateChatPosts/reloadCurrentIndex', privateChat.userId, { root: true })
                    commit('updatedViaChannel', {
                      type: 'privateChat',
                      id: privateChat.id
                    })
                  }
                }
              } else {
                let privateChatAttachmentsAttributes
                if (data.body.data.relationships.privateChatAttachments) {
                  const privateChatAttachmentIds = data.body.data.relationships.privateChatAttachments.data.map(privateChatAttachment => privateChatAttachment.id)
                  privateChatAttachmentsAttributes = data.body.included.filter(resource => resource.type === 'privateChatAttachment' && privateChatAttachmentIds.includes(resource.id))
                                                                       .map(privateChatAttachment => privateChatAttachment.attributes)
                }
                let privateChatReactions
                if (data.body.data.relationships.privateChatReactions) {
                  const privateChatReactionIds = data.body.data.relationships.privateChatReactions.data.map(privateChatReaction => privateChatReaction.id)
                  privateChatReactions = data.body.included.filter(resource => resource.type === 'privateChatReaction' && privateChatReactionIds.includes(resource.id))
                                                           .map(privateChatReaction => privateChatReaction.attributes)
                }
                PrivateChatPost.insert({
                  data: Object.assign(data.body.data.attributes, {
                    user: data.body.included.filter(resource => resource.type === 'user' && resource.id == data.body.data.attributes.userId)[0].attributes,
                    privateChatAttachmentsAttributes: privateChatAttachmentsAttributes,
                    privateChatReactions: privateChatReactions
                  })
                })
                commit('updatedViaChannel', {
                  type: 'privateChat',
                  id: data.body.data.attributes.privateChatId
                })
              }
              break
            }
            case 'reactPrivateChat': {
              let privateChatPost
              if (data.body.deletedPrivateChatReactionId) {
                privateChatPost = PrivateChatPost.find(data.body.privateChatPostId)
                if (privateChatPost) {
                  PrivateChatPost.update({
                    where: data.body.privateChatPostId,
                    data(privateChatPost) {
                      privateChatPost.privateChatReactions = privateChatPost.privateChatReactions.filter(reaction => {
                        return reaction.id != data.body.deletedPrivateChatReactionId
                      })
                    }
                  })
                }
              } else {
                privateChatPost = PrivateChatPost.find(data.body.data.attributes.privateChatPostId)
                if (privateChatPost) {
                  PrivateChatPost.update({
                    where: data.body.data.attributes.privateChatPostId,
                    data(privateChatPost) {
                      privateChatPost.privateChatReactions.push(data.body.data.attributes)
                    }
                  })
                }
              }
              if (privateChatPost) {
                commit('updatedViaChannel', {
                  type: 'privateChat',
                  id: privateChatPost.privateChatId
                })
              }
              break
            }
            case 'inviteVideoRoom': {
              if (data.body.deletedVideoRoomId) {
                VideoRoom.delete(data.body.deletedVideoRoomId)
              } else {
                VideoRoom.insert({
                  data: Object.assign(data.body.data.attributes, {
                    guestUsers: data.body.data.attributes.temporaryGuestUserIds.map((temporaryGuestUserId) => {
                      return data.body.included.filter((resource) => resource.type === 'user' && resource.id == temporaryGuestUserId)[0]?.attributes
                    })
                  })
                })
              }
              break
            }
            case 'syncProjectTask': {
              if (data.body.deletedTaskCardId) {
                TaskCard.delete(data.body.deletedTaskCardId)
              } else {
                TaskCard.insert({
                  data: data.body.data.attributes
                })
              }
              commit('entities/taskCards/syncRequest', true, { root: true })
              break
            }
          }
        }
      }
    )
  },
  connectOrganizationChannel({ rootGetters, state }) {
    state.cable.subscriptions.create(
      {
        channel: 'ClientOrganizationChannel',
        organization_id: rootGetters['auth/currentOrganization'].id
      },
      {
        received: (data) => {
          if (!data.type) return

          switch (data.type) {
            case 'changeUserStatus': {
              const user = User.find(data.body.data.attributes.userId)
              if (!user) return
              User.update({
                id: user.id,
                meta: Object.assign(user.meta, {
                  userPresence: data.body.data.attributes.userPresence,
                  userStatusType: data.body.data.attributes.userStatusType
                })
              })
              break
            }
          }
        }
      }
    )
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
